import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, LinearProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, Stack, IconButton, Tooltip, Collapse, MenuItem, TextField,
  CircularProgress, useTheme,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  TrendingUp as MrrIcon,
  EventSeat as SeatsIcon,
  CreditCard as SubIcon,
  Cancel as ChurnIcon,
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { platformApi } from '../api/services';

const STATUS_CHIP: Record<string, { color: 'success' | 'error' | 'warning' | 'default' | 'info' }> = {
  Active: { color: 'success' },
  Trialing: { color: 'info' },
  PastDue: { color: 'warning' },
  Paused: { color: 'error' },
  Cancelled: { color: 'default' },
};

function StatCard({ title, value, subtitle, icon, color }: {
  title: string; value: string; subtitle?: string; icon: React.ReactNode; color: string;
}) {
  return (
    <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2.5 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 700, lineHeight: 1.2 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

interface BillingOverview {
  mrrPence: number;
  mrrFormatted: string;
  arrPence: number;
  arrFormatted: string;
  totalTenants: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  pastDueSubscriptions: number;
  cancelledSubscriptions: number;
  totalActiveSeats: number;
  avgRevenuePerTenantPence: number;
  revenueByPlan: { plan: string; tenantCount: number; totalSeats: number; monthlyRevenuePence: number }[];
  subscriptionsByStatus: { active: number; trialing: number; pastDue: number; cancelled: number };
}

interface BillingTenant {
  id: string;
  tenantId: string;
  tenantName: string;
  plan: string;
  seatCount: number;
  monthlyAmountPence: number;
  status: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
  createdAt: string;
}

interface TenantBillingHistory {
  billing: any;
  events: { id: string; action: string; fromValue?: string; toValue?: string; performedByUserId?: string; timestamp: string }[];
  recentUsage: { year: number; month: number; planGenerations: number; transcriptsProcessed: number; aiTokensUsed: number }[];
}

export default function PlatformBillingPage() {
  const theme = useTheme();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [tenants, setTenants] = useState<BillingTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, TenantBillingHistory>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [planFilter, setPlanFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewData, tenantsData] = await Promise.all([
        platformApi.getBillingOverview(),
        platformApi.getBillingTenants({ status: statusFilter || undefined, plan: planFilter || undefined }),
      ]);
      setOverview(overviewData);
      setTenants(tenantsData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter]);

  useEffect(() => { load(); }, [load]);

  // Reset page on filter change
  useEffect(() => { setPage(0); }, [statusFilter, planFilter]);

  const loadHistory = useCallback(async (tenantId: string) => {
    setHistoryLoading(tenantId);
    try {
      const data = await platformApi.getTenantBillingHistory(tenantId);
      setHistory(prev => ({ ...prev, [tenantId]: data }));
    } catch {
      // Silently fail
    } finally {
      setHistoryLoading(null);
    }
  }, []);

  const toggleExpand = (tenantId: string) => {
    const isExpanding = expandedId !== tenantId;
    setExpandedId(prev => prev === tenantId ? null : tenantId);
    if (isExpanding && !history[tenantId]) {
      loadHistory(tenantId);
    }
  };

  const formatPence = (pence: number) => `\u00a3${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Get unique plans from tenants for filter
  const uniquePlans = Array.from(new Set(tenants.map(t => t.plan).filter(Boolean)));

  // --- Revenue Trend (MRR by month) ---
  const mrrTrend = useMemo(() => {
    if (tenants.length === 0) return [];
    const monthMap: Record<string, number> = {};
    tenants.forEach(t => {
      if (t.currentPeriodStart && t.status !== 'Cancelled') {
        const d = new Date(t.currentPeriodStart);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthMap[key] = (monthMap[key] || 0) + t.monthlyAmountPence;
      }
    });
    const months = Object.keys(monthMap).sort();
    return months.map(m => {
      const [year, mon] = m.split('-');
      const label = new Date(Number(year), Number(mon) - 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      return { month: label, mrr: monthMap[m] / 100 };
    });
  }, [tenants]);

  const hasMultipleMonths = mrrTrend.length > 1;

  // --- Churn Rate ---
  const churnData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const cancelledThisMonth = tenants.filter(t => {
      if (t.status !== 'Cancelled' || !t.cancelledAt) return false;
      const d = new Date(t.cancelledAt);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;

    // Active at start of month = current active + those cancelled this month
    const activeAtStart = (overview?.activeSubscriptions || 0) + (overview?.trialingSubscriptions || 0) + cancelledThisMonth;
    const churnRate = activeAtStart > 0 ? (cancelledThisMonth / activeAtStart) * 100 : 0;
    const churnColor = churnRate < 5 ? 'success' : churnRate <= 10 ? 'warning' : 'error';

    return { cancelledThisMonth, activeAtStart, churnRate, churnColor };
  }, [tenants, overview]);

  // --- Dunning Stage Label ---
  const getDunningLabel = (tenant: BillingTenant): { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' } => {
    switch (tenant.status) {
      case 'Active':
        return { label: 'Active', color: 'success' };
      case 'Trialing':
        return { label: 'Trialing', color: 'info' };
      case 'PastDue': {
        const daysPastDue = tenant.currentPeriodEnd
          ? Math.max(0, Math.floor((Date.now() - new Date(tenant.currentPeriodEnd).getTime()) / (1000 * 60 * 60 * 24)))
          : 0;
        return { label: `Past Due — Day ${daysPastDue} of 5`, color: 'warning' };
      }
      case 'Paused': {
        const daysPaused = tenant.cancelledAt
          ? Math.floor((Date.now() - new Date(tenant.cancelledAt).getTime()) / (1000 * 60 * 60 * 24))
          : 0;
        return { label: `Paused — Day ${daysPaused} — suspended`, color: 'error' };
      }
      case 'Cancelled':
        return { label: 'Cancelled', color: 'default' };
      default:
        return { label: tenant.status, color: 'default' };
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <PaymentIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Billing Centre</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Revenue, subscriptions, and tenant billing
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress color="error" sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Past Due Alert */}
      {overview && overview.pastDueSubscriptions > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <strong>{overview.pastDueSubscriptions} subscription{overview.pastDueSubscriptions > 1 ? 's' : ''} past due</strong> — these tenants may have payment issues that need attention.
        </Alert>
      )}

      {overview && (
        <Stack spacing={2.5}>
          {/* Revenue Summary Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Monthly Recurring Revenue"
                value={overview.mrrFormatted}
                icon={<MrrIcon />}
                color={theme.palette.success.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Annual Run Rate"
                value={overview.arrFormatted}
                subtitle="MRR x 12"
                icon={<MrrIcon />}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active Subscriptions"
                value={String(overview.activeSubscriptions)}
                subtitle={`${overview.trialingSubscriptions} trialing`}
                icon={<SubIcon />}
                color={theme.palette.secondary.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Active Seats"
                value={String(overview.totalActiveSeats)}
                subtitle={overview.activeSubscriptions > 0 ? `Avg ${formatPence(overview.avgRevenuePerTenantPence)}/tenant` : undefined}
                icon={<SeatsIcon />}
                color={theme.palette.warning.main}
              />
            </Grid>
          </Grid>

          {/* Revenue Trend + Churn */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Revenue Trend
                  </Typography>
                  {hasMultipleMonths ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={mrrTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <defs>
                          <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary, fontSize: 12 }} />
                        <YAxis
                          tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                          tickFormatter={(v: number) => `\u00a3${v.toLocaleString()}`}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            color: theme.palette.text.primary,
                          }}
                          formatter={(value: any) => [`\u00a3${Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`, 'MRR']}
                        />
                        <Area
                          type="monotone"
                          dataKey="mrr"
                          stroke={theme.palette.success.main}
                          strokeWidth={2}
                          fill="url(#mrrGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700, mb: 1 }}>
                        {overview?.mrrFormatted || '\u00a30.00'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Current MRR
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                        Historical trend available after 3 months of data.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Churn
                  </Typography>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: `${churnData.churnColor}.main`,
                        lineHeight: 1.2,
                      }}
                    >
                      {churnData.churnRate.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      Monthly churn rate
                    </Typography>
                  </Box>
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Cancelled this month
                      </Typography>
                      <Chip
                        icon={<ChurnIcon sx={{ fontSize: 14 }} />}
                        label={`${churnData.cancelledThisMonth} tenant${churnData.cancelledThisMonth !== 1 ? 's' : ''}`}
                        size="small"
                        color={churnData.churnColor as any}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Active at month start
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {churnData.activeAtStart}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(churnData.churnRate, 100)}
                      sx={{
                        height: 6, borderRadius: 3, mt: 1,
                        bgcolor: (t) => t.palette.action.hover,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: (t) =>
                            churnData.churnColor === 'success' ? t.palette.success.main
                            : churnData.churnColor === 'warning' ? t.palette.warning.main
                            : t.palette.error.main,
                          borderRadius: 3,
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center', display: 'block' }}>
                      {churnData.churnRate < 5 ? 'Healthy' : churnData.churnRate <= 10 ? 'Needs attention' : 'Critical'}
                      {' — '}target: &lt;5%
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Revenue by Plan + Subscription Status */}
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Revenue by Plan
                  </Typography>
                  {overview.revenueByPlan.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Plan</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Tenants</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Seats</TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Monthly Revenue</TableCell>
                            <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, width: '30%' }}>Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overview.revenueByPlan.map(row => {
                            const totalRev = overview.revenueByPlan.reduce((s, r) => s + r.monthlyRevenuePence, 0);
                            const pct = totalRev > 0 ? (row.monthlyRevenuePence / totalRev) * 100 : 0;
                            return (
                              <TableRow key={row.plan}>
                                <TableCell>
                                  <Chip label={row.plan} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{row.tenantCount}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{row.totalSeats}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    {formatPence(row.monthlyRevenuePence)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct}
                                      sx={{
                                        flex: 1, height: 6, borderRadius: 3,
                                        bgcolor: (t) => t.palette.action.hover,
                                        '& .MuiLinearProgress-bar': { bgcolor: theme.palette.success.main, borderRadius: 3 },
                                      }}
                                    />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 32, textAlign: 'right' }}>
                                      {pct.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No revenue data yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card sx={{ bgcolor: 'background.paper', height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                    Subscription Status
                  </Typography>
                  <Stack spacing={2}>
                    {[
                      { label: 'Active', count: overview.subscriptionsByStatus.active, color: theme.palette.success.main },
                      { label: 'Trialing', count: overview.subscriptionsByStatus.trialing, color: theme.palette.primary.main },
                      { label: 'Past Due', count: overview.subscriptionsByStatus.pastDue, color: theme.palette.error.main },
                      { label: 'Cancelled', count: overview.subscriptionsByStatus.cancelled, color: theme.palette.text.secondary },
                    ].map(item => {
                      const total = overview.activeSubscriptions + overview.trialingSubscriptions + overview.pastDueSubscriptions + overview.cancelledSubscriptions;
                      const pct = total > 0 ? (item.count / total) * 100 : 0;
                      return (
                        <Box key={item.label}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.label}</Typography>
                            <Typography variant="body2" sx={{ color: item.color, fontWeight: 600 }}>
                              {item.count}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 6, borderRadius: 3,
                              bgcolor: (t) => t.palette.action.hover,
                              '& .MuiLinearProgress-bar': { bgcolor: item.color, borderRadius: 3 },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tenant Billing Table */}
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Tenant Billing
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Trialing">Trialing</MenuItem>
                    <MenuItem value="PastDue">Past Due</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Plan"
                    value={planFilter}
                    onChange={e => setPlanFilter(e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniquePlans.map(p => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, width: 40 }} />
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Tenant</TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Plan</TableCell>
                      <TableCell align="center" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Seats</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Monthly</TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Period</TableCell>
                      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600 }}>Stripe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(t => (
                      <React.Fragment key={t.id}>
                        <TableRow
                          hover
                          sx={{ cursor: 'pointer', '& td': { borderColor: 'divider' } }}
                          onClick={() => toggleExpand(t.tenantId)}
                        >
                          <TableCell sx={{ width: 40, pr: 0 }}>
                            <IconButton size="small">
                              {expandedId === t.tenantId ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {t.tenantName}
                          </TableCell>
                          <TableCell>
                            <Chip label={t.plan} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>{t.seatCount}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                              {formatPence(t.monthlyAmountPence)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const dunning = getDunningLabel(t);
                              return (
                                <Chip
                                  label={dunning.label}
                                  size="small"
                                  color={dunning.color}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {t.currentPeriodStart
                                ? `${new Date(t.currentPeriodStart).toLocaleDateString()} - ${new Date(t.currentPeriodEnd!).toLocaleDateString()}`
                                : '---'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {t.stripeCustomerId ? (
                              <Tooltip title={`Customer: ${t.stripeCustomerId}${t.stripeSubscriptionId ? ` | Sub: ${t.stripeSubscriptionId}` : ''}`}>
                                <Chip label="Linked" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                              </Tooltip>
                            ) : (
                              <Chip label="No Stripe" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderColor: 'divider' }}>
                            <Collapse in={expandedId === t.tenantId} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2 }}>
                                {historyLoading === t.tenantId ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : history[t.tenantId] ? (
                                  <Stack spacing={2}>
                                    {/* Stripe IDs */}
                                    <Stack direction="row" spacing={4} flexWrap="wrap">
                                      <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tenant ID</Typography>
                                        <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                          {t.tenantId}
                                        </Typography>
                                      </Box>
                                      {t.stripeCustomerId && (
                                        <Box>
                                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stripe Customer</Typography>
                                          <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {t.stripeCustomerId}
                                          </Typography>
                                        </Box>
                                      )}
                                      {t.stripeSubscriptionId && (
                                        <Box>
                                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Stripe Subscription</Typography>
                                          <Typography variant="body2" sx={{ color: 'text.primary', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                            {t.stripeSubscriptionId}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Stack>

                                    {/* Subscription Events */}
                                    {history[t.tenantId].events.length > 0 && (
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                          Subscription History
                                        </Typography>
                                        <TableContainer>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Action</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>From</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>To</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {history[t.tenantId].events.map(e => (
                                                <TableRow key={e.id}>
                                                  <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    <Chip label={e.action} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                                  </TableCell>
                                                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {e.fromValue || '---'}
                                                  </TableCell>
                                                  <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {e.toValue || '---'}
                                                  </TableCell>
                                                  <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {new Date(e.timestamp).toLocaleString()}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    )}

                                    {/* Recent Usage */}
                                    {history[t.tenantId].recentUsage.length > 0 && (
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                          Recent Usage
                                        </Typography>
                                        <TableContainer>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Period</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Plans</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>Transcripts</TableCell>
                                                <TableCell align="right" sx={{ color: 'text.secondary', borderColor: 'divider', fontWeight: 600, fontSize: '0.75rem' }}>AI Tokens</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {history[t.tenantId].recentUsage.map(u => (
                                                <TableRow key={`${u.year}-${u.month}`}>
                                                  <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {new Date(u.year, u.month - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {u.planGenerations}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {u.transcriptsProcessed}
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ color: 'text.primary', borderColor: 'divider', fontSize: '0.8rem' }}>
                                                    {u.aiTokensUsed.toLocaleString()}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Box>
                                    )}

                                    {history[t.tenantId].events.length === 0 && history[t.tenantId].recentUsage.length === 0 && (
                                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                                        No history or usage data available for this tenant.
                                      </Typography>
                                    )}
                                  </Stack>
                                ) : (
                                  <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
                                    No billing data available.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                    {!loading && tenants.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>No billing records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={tenants.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </Stack>
      )}

      {!loading && !overview && !error && (
        <Card sx={{ bgcolor: 'background.paper' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">No billing data available</Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
